import { AddRounded } from '@mui/icons-material';
import { Box, Button, SxProps, Theme } from '@mui/material';

import { useLanguage } from '../../hooks';
import { MeterConfiguration } from '../../types';
import { SelectedAssets } from '../../types/questions';
import { InformationBanner } from '../uiComponents/CommonComponents/InformationBanner';
import { MeterConfigItem } from './MeterConfigItem';

interface MeterSettingProps {
  questionIndex: number;
  questionId: string;
  meterConfigurations: MeterConfiguration[];
  tableId?: string;
  onAddMeterConfiguration: (questionId: string, tableId?: string) => void;
  onDeleteMeterConfiguration: (questionId: string, configIndex: number, tableId?: string) => void;
  selectedAssets?: SelectedAssets;
  onMeterConfigurationMeterChange: (
    questionId: string,
    configIndex: number,
    newMeterId: string,
    tableId?: string,
  ) => void;
  onAddMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    tableId?: string,
  ) => void;
  onUpdateMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    questionSlotIndex: number,
    value: string,
    tableId?: string,
  ) => void;
  onDeleteMeterConfigurationQuestion: (
    questionId: string,
    configIndex: number,
    questionSlotIndex: number,
    tableId?: string,
  ) => void;
  onCreateAssetQuestion: (
    index: number,
    tableId: string | undefined,
    type: 'number' | 'select_metadata',
  ) => void;
}

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  addMeterButton: {
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 500,
    alignSelf: 'stretch',
    borderRadius: '16px',
    '& svg': {
      width: '16px',
      height: '16px',
    },
  },
};

export function MeterSetting({
  questionIndex,
  questionId,
  meterConfigurations,
  tableId,
  onAddMeterConfiguration,
  onDeleteMeterConfiguration,
  onMeterConfigurationMeterChange,
  onAddMeterConfigurationQuestion,
  onUpdateMeterConfigurationQuestion,
  onDeleteMeterConfigurationQuestion,
  onCreateAssetQuestion,
  selectedAssets,
}: MeterSettingProps) {
  const lang = useLanguage();

  const effectiveConfigs =
    meterConfigurations.length > 0
      ? meterConfigurations
      : [{ meterId: '', associatedQuestions: [] }];

  const usedMeterIds = effectiveConfigs
    .map((cfg) => (cfg.isLocation ? 'nestable_location' : cfg.meterId))
    .filter(Boolean);

  return (
    <Box sx={customStyles.root}>
      <InformationBanner message={lang.form_builder.meterConfigBannerMeter} />
      {effectiveConfigs.map((config, configIndex) => {
        const usedQuestionIdsFromOtherConfigs = effectiveConfigs
          .filter((_, i) => i !== configIndex)
          .flatMap((cfg) => cfg.associatedQuestions)
          .filter(Boolean);

        return (
          <MeterConfigItem
            key={`meter-config-${configIndex}`}
            questionIndex={questionIndex}
            questionId={questionId}
            configIndex={configIndex}
            config={config}
            tableId={tableId}
            usedMeterIds={usedMeterIds}
            usedQuestionIds={usedQuestionIdsFromOtherConfigs}
            canDelete={effectiveConfigs.length > 1}
            selectedAssets={selectedAssets}
            onDelete={() => onDeleteMeterConfiguration(questionId, configIndex, tableId)}
            onMeterChange={(newMeterId) =>
              onMeterConfigurationMeterChange(questionId, configIndex, newMeterId, tableId)
            }
            onAddQuestion={() => onAddMeterConfigurationQuestion(questionId, configIndex, tableId)}
            onUpdateQuestion={(slotIndex, value) =>
              onUpdateMeterConfigurationQuestion(questionId, configIndex, slotIndex, value, tableId)
            }
            onDeleteQuestion={(slotIndex) =>
              onDeleteMeterConfigurationQuestion(questionId, configIndex, slotIndex, tableId)
            }
            onCreateAssetQuestion={onCreateAssetQuestion}
          />
        );
      })}

      <Button
        variant='outlined'
        color='primary'
        endIcon={<AddRounded />}
        onClick={() => onAddMeterConfiguration(questionId, tableId)}
        sx={customStyles.addMeterButton}
      >
        {lang.form_builder.addMeterConfiguration}
      </Button>
    </Box>
  );
}
